import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
} from '@roger/ui-components';
import { OASISSchema, OASISFormData, OASISNavigationItem } from './types';
import { Circle, Check, AlertTriangle } from 'lucide-react';

interface OASISNavigationProps {
  categories: string[];
  currentCategory: string | null;
  onCategoryChange: (category: string) => void;
  schema: OASISSchema;
  formData: OASISFormData;
}

export const OASISNavigation: React.FC<OASISNavigationProps> = ({
  categories,
  currentCategory,
  onCategoryChange,
  schema,
  formData,
}) => {
  // Calculate completion status for each category (excluding pseudo questions)
  const getNavigationItems = (): OASISNavigationItem[] => {
    return categories.map(category => {
      const questions = schema[category] || [];
      // Only count non-pseudo questions
      const nonPseudoQuestions = questions.filter(q => q.is_pseudo !== true);
      const questionCount = nonPseudoQuestions.length;
      let answeredCount = 0;

      // Count answered non-pseudo questions
      nonPseudoQuestions.forEach(question => {
        const answer = formData[question.qid];
        if (answer !== undefined && answer !== null && answer !== '') {
          // For arrays (multiselect), check if not empty
          if (Array.isArray(answer)) {
            if (answer.length > 0) answeredCount++;
          } else {
            answeredCount++;
          }
        }
      });

      return {
        category,
        label: category
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim(),
        isCompleted: answeredCount === questionCount && questionCount > 0,
        questionCount,
        answeredCount,
      };
    });
  };

  const navigationItems = getNavigationItems();

  const getCompletionIcon = (item: OASISNavigationItem) => {
    if (item.questionCount === 0) {
      return (
        <div className='relative h-4 w-4'>
          <Circle className='h-4 w-4 text-gray-400' />
          <AlertTriangle className='h-2.5 w-2.5 text-gray-400 absolute top-0.5 left-0.5' />
        </div>
      );
    }

    if (item.isCompleted) {
      return (
        <div className='relative h-4 w-4'>
          <Circle className='h-4 w-4 text-green-600 fill-green-100' />
          <Check className='h-2.5 w-2.5 text-green-600 absolute top-0.5 left-0.5' />
        </div>
      );
    }

    if (item.answeredCount > 0) {
      return (
        <div className='relative h-4 w-4'>
          <Circle className='h-4 w-4 text-blue-600 fill-blue-600' />
        </div>
      );
    }

    return (
      <div className='relative h-4 w-4'>
        <Circle className='h-4 w-4 text-gray-400' />
      </div>
    );
  };

  const getCompletionText = (item: OASISNavigationItem) => {
    if (item.questionCount === 0) return 'No questions';
    return `${item.answeredCount}/${item.questionCount}`;
  };

  const getCompletionBadgeVariant = (item: OASISNavigationItem) => {
    if (item.questionCount === 0) return 'secondary';
    if (item.isCompleted) return 'default';
    if (item.answeredCount > 0) return 'secondary';
    return 'outline';
  };

  return (
    <Card className='h-fit'>
      <CardHeader className='pb-3'>
        <CardTitle className='text-lg'>OASIS Sections</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        {navigationItems.map(item => (
          <button
            key={item.category}
            onClick={() => onCategoryChange(item.category)}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${
              currentCategory === item.category
                ? 'bg-blue-50 border-blue-200 text-blue-900'
                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700 hover:text-gray-900'
            }`}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                {getCompletionIcon(item)}
                <div>
                  <div className='font-medium text-sm'>{item.label}</div>
                  <div className='text-xs text-gray-500'>
                    {item.questionCount} questions
                  </div>
                </div>
              </div>
              <Badge
                variant={getCompletionBadgeVariant(item)}
                className='text-xs'
              >
                {getCompletionText(item)}
              </Badge>
            </div>
          </button>
        ))}

        {/* Overall progress summary */}
        <div className='mt-6 pt-4 border-t border-gray-200'>
          <div className='text-sm text-gray-600 space-y-1'>
            <div className='flex justify-between'>
              <span>Total Progress:</span>
              <span className='font-medium'>
                {navigationItems.reduce(
                  (sum, item) => sum + item.answeredCount,
                  0
                )}{' '}
                /{' '}
                {navigationItems.reduce(
                  (sum, item) => sum + item.questionCount,
                  0
                )}
              </span>
            </div>
            <div className='flex justify-between'>
              <span>Completed Sections:</span>
              <span className='font-medium'>
                {navigationItems.filter(item => item.isCompleted).length} /{' '}
                {navigationItems.length}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
